import {
  DEFAULT_CONFIG,
  HEAD_SPAWN_TIMER_MS,
  HEAD_SPAWN_ALPHA_MS,
  HEAD_SPAWN_SCALE_DELAY_MS,
  HEAD_SPAWN_SCALE_MS,
  HEAD_SPAWN_MARKER_FALLBACK_MS,
  POWERUP_SPAWN_ANIM_MS,
  POWERUP_RADIUS,
  EFFECT_RING_SEGMENTS,
  EFFECT_RING_DISTANCE_STEP,
  EFFECT_RING_DISTANCE_OFFSET,
  EFFECT_RING_LINE_WIDTH,
  EFFECT_RING_MAX_STACKS,
  EFFECT_RING_SPEED_COLOR,
  PU_TYPES,
  PLAYER_FLAG_ALIVE,
  PLAYER_FLAG_SPEED,
  PLAYER_FLAG_LOCAL,
  PLAYER_FLAG_DRAWING_TRAIL,
  POWERUP_FLAG_CLEAR,
  POWERUP_FLAG_GEM,
  POWERUP_POS_STRIDE,
  DEFAULT_POWERUP_ICON_SRCS,
  LOCAL_HEAD_ACTIVE_ATTACH_MAX_PX
} from '../engine/config.js';
import {
  powerupsCanvas,
  trailCanvas,
  headsCanvas,
  arenaFrameEl,
  arenaEl,
  roundOverlayEl,
  focusOverlayEl
} from '../ui/dom.js';
import { authoritative, model, net } from '../engine/state.js';
import { settings } from '../ui/settings.js';
import {
  clampTrailPercent,
  slotTrailOpacity,
  slotTrailOpacityPercent,
  trailOpacitySignature
} from '../engine/trail-opacity.js';
import { clamp } from '../utils/utils.js';
import { stateTickNow } from '../engine/time.js';
import { speedEffectStackCount, speedEffectTotalTicks } from '../render/effects.js';
import {
  applyTrailOpsCore,
  drawHeadsCore,
  localTurnHintDirtyExtent,
  setTrailPaintResolver,
  spawnTweenDirtyExtent
} from '../../core/render-core.js';
import { sampleGradientStops } from './color.js';
import { normalizeTeam, teamHeadShapeFor } from '/shared/teams.js';
import { DISCONNECTED_COLOR } from '/shared/game-engine.js';

let trailCtx = null;
let headsCtx = null;
let powerupsCtx = null;
// Every canvas the stage lays out. A function rather than a constant because
// the trail can grow extra canvases at runtime -- see trailSurfaceForPercent.
function canvasLayers() {
  return [powerupsCanvas, ...trailSurfaceCanvases(), headsCanvas];
}
const POWERUP_ICON_SCALE = 1.3;
const GEM_ICON_SCALE = 1.78;
const GEM_ICON_CENTER_Y_OFFSET = 0.08;
const REMOTE_LIVE_TRAIL_BRIDGE_MAX_PX = 16;
const HEAD_DIRTY_RECT_PADDING_PX = 3;
const GEM_COLLECTION_EFFECT_DURATION_MS = 720;
const GEM_COLLECTION_EFFECT_MAX_ACTIVE = 24;
const GEM_COLLECTION_POPUP_FONT = '800 25px "Manrope", "Avenir Next", "Segoe UI", sans-serif';
// --- Mesh colors ------------------------------------------------------------
//
// A mesh color paints as one linear gradient along the arena diagonal, so a
// trail's hue depends on WHERE it was drawn rather than on the snake. That
// falls out of the existing drawing for free: trail ops are already in field
// coordinates on every surface (each owner layer carries its own field ->
// pixel transform), so a gradient defined in field coordinates lines up on all
// of them, and segments drawn at different times still agree.
//
// Ops carry the player's flat hex, not their mesh, so the lookup is keyed on
// that hex -- roster colors are unique per match, so it identifies the player.
const meshStopsByColor = new Map();
// Gradients belong to the context that created them, hence per-ctx caches.
const meshGradientsByCtx = new WeakMap();

function meshFieldSize() {
  return {
    width: Math.max(1, Number(model.config?.fieldWidth) || DEFAULT_CONFIG.fieldWidth),
    height: Math.max(1, Number(model.config?.fieldHeight) || DEFAULT_CONFIG.fieldHeight)
  };
}

function syncMeshColors(players) {
  const items = Array.isArray(players) ? players : [];
  for (const player of items) {
    const color = typeof player?.color === 'string' ? player.color : '';
    if (!color) continue;
    const stops = Array.isArray(player?.mesh) && player.mesh.length > 1 ? player.mesh : null;
    if (stops) {
      const known = meshStopsByColor.get(color);
      if (!known || known.join() !== stops.join()) meshStopsByColor.set(color, stops.slice());
    } else if (meshStopsByColor.has(color)) {
      meshStopsByColor.delete(color);
    }
  }
}

function meshGradientFor(ctx, color) {
  const stops = meshStopsByColor.get(color);
  if (!stops) return null;
  const { width, height } = meshFieldSize();
  const key = `${color}|${width}x${height}`;
  let byKey = meshGradientsByCtx.get(ctx);
  if (!byKey) {
    byKey = new Map();
    meshGradientsByCtx.set(ctx, byKey);
  }
  const cached = byKey.get(key);
  if (cached) return cached;
  let gradient = null;
  try {
    gradient = ctx.createLinearGradient(0, 0, width, height);
    for (let i = 0; i < stops.length; i++) {
      gradient.addColorStop(i / (stops.length - 1), stops[i]);
    }
  } catch (_) {
    return null;
  }
  byKey.set(key, gradient);
  return gradient;
}

// Where a point sits along the arena diagonal, 0..1 -- the same parameter the
// gradient uses, so sampling here matches what the trail paints there.
function meshParamAt(x, y) {
  const { width, height } = meshFieldSize();
  const denom = (width * width) + (height * height);
  if (denom <= 0) return 0;
  return clamp((((Number(x) || 0) * width) + ((Number(y) || 0) * height)) / denom, 0, 1);
}

function meshColorAtPosition(player, flatColor) {
  const stops = meshStopsByColor.get(flatColor);
  if (!stops) return flatColor;
  return sampleGradientStops(stops, meshParamAt(player?.x, player?.y)) || flatColor;
}

setTrailPaintResolver((ctx, color) => meshGradientFor(ctx, color));

// Trails render semi-transparent, and the alpha is applied to a trail CANVAS
// rather than to each stroke: the trail is an accumulating surface, so a
// per-stroke alpha would blend twice everywhere strokes meet -- at every batch
// seam, every round join, every self-crossing -- and read as banding along the
// trail. One alpha on a whole layer is uniform by construction, and free (the
// compositor already owns the layer). The live trail bridge is drawn on the
// heads canvas instead, so it applies the same factor itself to stay flush with
// the trail it continues.
//
// Opacity is per server-owned SLOT, which that law turns into a layout rule:
// slots that share an alpha share a canvas, and a slot with its own alpha gets
// a canvas of its own. A fully solid room still runs on exactly one canvas, so
// drawing stays direct -- no per-frame composite and no retained-layer blur.
//
// Clamped to the same floor the slider enforces: the trail is the obstacle you
// have to read to survive, so it must never reach invisible.
const trailSurfacesByPercent = new Map();

function trailSurfaceCanvases() {
  const canvases = [];
  for (const surface of trailSurfacesByPercent.values()) {
    if (surface?.canvas) canvases.push(surface.canvas);
  }
  // The base canvas is in the map only once something has been drawn. Keep it
  // in the layout from the first frame either way.
  if (!canvases.includes(trailCanvas) && trailCanvas) canvases.unshift(trailCanvas);
  return canvases;
}

// A clone of the base trail canvas: same cell, same size, same inline layout
// the stage fit last handed out. It goes immediately after the base so DOM
// order decides z-order among trail canvases, which all share one z-index.
function createExtraTrailCanvas() {
  if (!trailCanvas || typeof document === 'undefined') return null;
  const canvas = document.createElement('canvas');
  canvas.className = 'trail-alpha-canvas';
  canvas.width = trailCanvas.width;
  canvas.height = trailCanvas.height;
  for (const prop of ['left', 'top', 'width', 'height', 'visibility']) {
    canvas.style[prop] = trailCanvas.style[prop];
  }
  // Placement copied from the base rather than trusted to match by stylesheet.
  // A rule that places #game and forgets .trail-alpha-canvas does not misplace
  // the trail, it just loses this belt-and-braces copy.
  const baseStyle = typeof getComputedStyle === 'function' ? getComputedStyle(trailCanvas) : null;
  if (baseStyle) {
    canvas.style.gridColumn = baseStyle.gridColumnStart;
    canvas.style.gridRow = baseStyle.gridRowStart;
    canvas.style.justifySelf = baseStyle.justifySelf;
    canvas.style.alignSelf = baseStyle.alignSelf;
  }
  const anchor = [...trailSurfaceCanvases()].pop() || trailCanvas;
  anchor.insertAdjacentElement('afterend', canvas);
  return canvas;
}

function baseTrailCanvasIsClaimed() {
  for (const surface of trailSurfacesByPercent.values()) {
    if (surface.canvas === trailCanvas) return true;
  }
  return false;
}

function trailSurfaceForPercent(percent) {
  const key = clampTrailPercent(percent);
  const existing = trailSurfacesByPercent.get(key);
  if (existing) return existing;
  // The canvas in the markup is claimed by whichever alpha needs one first, and
  // handed on rather than left idle if that alpha later goes away. Merged mode
  // never claims it: that one is the composite target, and the trail has to
  // accumulate somewhere the frame can blit FROM.
  const canvas = (!ARENA_MERGED && !baseTrailCanvasIsClaimed())
    ? trailCanvas
    : createExtraTrailCanvas();
  if (!canvas) return null;
  const surface = { percent: key, canvas, ctx: null };
  trailSurfacesByPercent.set(key, surface);
  canvas.style.opacity = String(key / 100);
  resizeLayerBacking(canvas, renderSurface.backingWidth, renderSurface.backingHeight);
  return surface;
}

function trailSurfaceCtx(surface) {
  if (!surface) return null;
  if (surface.ctx) return surface.ctx;
  // Accumulating surface: see the note above renderContext2dOptions.
  surface.ctx = getCanvas2dContext(surface.canvas, { desynchronized: false });
  if (surface.ctx) applyCanvasTransform(surface.ctx);
  return surface.ctx;
}

// The context an owner's strokes belong on. Ops with no owner (a clear, a
// legacy op) go on the base surface, which is solid.
function trailCtxForOwner(ownerId) {
  const percent = ownerId === null || ownerId === undefined
    ? 100
    : slotTrailOpacityPercent(ownerId);
  return trailSurfaceCtx(trailSurfaceForPercent(percent));
}

function forEachTrailSurfaceCtx(fn) {
  for (const surface of trailSurfacesByPercent.values()) {
    const ctx = trailSurfaceCtx(surface);
    if (ctx) fn(ctx, surface);
  }
}

// A canvas nobody is on any more: drop it so a room does not accumulate one
// canvas per alpha ever used. The base is kept -- it is in the page's markup.
function retireEmptyTrailSurfaces(livePercents) {
  for (const [percent, surface] of [...trailSurfacesByPercent]) {
    if (livePercents.has(percent)) continue;
    // Clear before letting go: a retired surface that still held pixels would
    // leave a ghost of the alpha nobody is drawn at any more.
    const ctx = trailSurfaceCtx(surface);
    if (ctx) clearCanvasContext(ctx);
    trailSurfacesByPercent.delete(percent);
    if (surface.canvas === trailCanvas) {
      if (trailCanvas) trailCanvas.style.opacity = '1';
      continue;
    }
    surface.canvas?.remove();
  }
}

// Re-applied when an alpha changes rather than only when the canvases are first
// shown. Cheap enough to check once a frame: the signature is a short string
// built from the map of dimmed slots, which is empty in the common case.
let appliedTrailOpacitySignature = null;
function syncTrailOpacityStyle() {
  const next = trailOpacitySignature();
  if (next === appliedTrailOpacitySignature) return;
  const first = appliedTrailOpacitySignature === null;
  appliedTrailOpacitySignature = next;
  // Which alphas are still wanted, by the owners that actually have pixels.
  const livePercents = new Set();
  for (const ownerId of trailLayersByOwner.keys()) {
    livePercents.add(ownerId < 0 ? 100 : slotTrailOpacityPercent(ownerId));
  }
  if (livePercents.size === 0) livePercents.add(100);
  for (const percent of livePercents) trailSurfaceForPercent(percent);
  retireEmptyTrailSurfaces(livePercents);
  for (const surface of trailSurfacesByPercent.values()) {
    if (!surface.canvas) continue;
    surface.canvas.style.opacity = String(surface.percent / 100);
  }
  // Owners may have moved between canvases, and only the retained layers know
  // what each of them had drawn -- so the surfaces are rebuilt from those.
  // Skipped on the first call, where there is nothing drawn yet to move.
  if (!first) compositeTrailOwnerLayers();
}
const ENABLE_CANVAS_ANTIALIAS = true;
const MAX_RENDER_PIXEL_RATIO = 2;
// A CANVAS MEMORY BUDGET, not just a resolution ceiling.
//
// Measured on a reader's Android device: a 964px arena at DPR 2 gave a
// 1928x1928 backing store -- 3.7 megapixels, 15MB, and one of those for EVERY
// visible canvas plus one retained layer per player. Around 60MB of canvas on a
// page. Chrome for Android stops DISPLAYING canvases when a page runs past its
// canvas budget while getImageData keeps working, which is exactly what that
// device reported: pixels present, element visible and unobstructed, nothing on
// screen -- and a modal fixed it, because a backdrop filter rasterizes the
// bitmap into the page instead of relying on the canvas's own layer.
//
// The budget applies where it is needed. A desktop keeps the old ceiling, which
// was a resolution limit and cost nothing; a touch device gets a real budget.
// Coarse pointer rather than a memory reading, because the constraint is the
// browser's canvas budget on phones and tablets, not how much RAM the device
// happens to have -- and deviceMemory is not reported at all in some browsers.
const LOW_CANVAS_BUDGET = (() => {
  try {
    // Any one of these is enough. A coarse pointer is the direct signal, but a
    // single signal that a browser reports differently would leave the device
    // it was meant for unprotected -- and being wrong here costs a desktop some
    // sharpness, while being wrong the other way costs a phone the whole trail.
    if (window.matchMedia('(pointer: coarse)').matches) return 'coarse';
    if (window.matchMedia('(hover: none)').matches) return 'hover';
    const memory = Number(navigator.deviceMemory);
    if (Number.isFinite(memory) && memory > 0 && memory <= 4) return 'mem';
    if (Math.min(screen.width, screen.height) <= 1024) return 'screen';
    // Deliberately NOT maxTouchPoints: touch-capable laptops are common and
    // have the budget to spare, and this only needs to catch the devices that
    // do not.
    return false;
  } catch (_) {
    return false;
  }
})();
// 2.6M is a 1600x1600 arena -- more resolution than a 720-unit field can use on
// any phone -- and takes a page that measured ~60MB down to roughly 25.
const MAX_RENDER_SURFACE_PIXELS = LOW_CANVAS_BUDGET ? 2_600_000 : 8_000_000;
const powerupIconCache = new Map();
const circlePathCache = new Map();
const powerupRenderCache = {
  signature: '',
  dirty: true,
  wasAnimating: false
};
const pendingTrailOps = [];
const trailAnchors = new Map();
// Trail pixels are retained once per player, on canvases matching the backing
// store (see MAX_TRAIL_LAYER_PIXELS). A death can then drop one layer and
// composite the remaining players in a handful of drawImage calls instead of
// replaying every segment accumulated during the round on the input/render
// thread -- and because the layers are not coarser than what they are drawn
// back onto, that composite is pixel-identical to the trail it replaces.
const trailLayersByOwner = new Map();
let trailCompositeDirty = false;
const gemCollectionEffects = [];
let previousHeadDirtyRects = [];
let previousHeadFrameWasComplex = false;
let headsNeedFullClear = true;
const renderSurface = {
  fieldWidth: DEFAULT_CONFIG.fieldWidth,
  fieldHeight: DEFAULT_CONFIG.fieldHeight,
  cssWidth: DEFAULT_CONFIG.fieldWidth,
  cssHeight: DEFAULT_CONFIG.fieldHeight,
  backingWidth: DEFAULT_CONFIG.fieldWidth,
  backingHeight: DEFAULT_CONFIG.fieldHeight,
  scaleX: 1,
  scaleY: 1,
  pixelRatio: 1
};
const HEAD_SPAWN_RENDER_CONFIG = Object.freeze({
  timerMs: HEAD_SPAWN_TIMER_MS,
  alphaMs: HEAD_SPAWN_ALPHA_MS,
  scaleDelayMs: HEAD_SPAWN_SCALE_DELAY_MS,
  scaleMs: HEAD_SPAWN_SCALE_MS,
  markerFallbackMs: HEAD_SPAWN_MARKER_FALLBACK_MS
});
const HEAD_EFFECT_RENDER_CONFIG = {
  speedType: PU_TYPES.SPEED,
  ringSegments: EFFECT_RING_SEGMENTS,
  ringDistanceStep: EFFECT_RING_DISTANCE_STEP,
  ringDistanceOffset: EFFECT_RING_DISTANCE_OFFSET,
  ringLineWidth: EFFECT_RING_LINE_WIDTH,
  ringMaxStacks: EFFECT_RING_MAX_STACKS,
  ringColor: EFFECT_RING_SPEED_COLOR,
  resolveStacks: speedEffectStackCount,
  resolveTotalTicks: () => speedEffectTotalTicks()
};

// `desynchronized` is Chrome's low-latency canvas hint: it may present the
// canvas through a swap chain that need not preserve what was drawn last frame.
// Safe for a surface rebuilt every frame, not for one that accumulates -- so
// the trail, which is only ever drawn incrementally, does not take it.
//
// This was also the first theory for the Android trail bug and it was WRONG
// (turning it off changed nothing there; see ARENA_MERGED for the real cause).
// It stays because it is the correct setting for an accumulating canvas on its
// own merits, and it costs nothing.
//
// Heads keep the hint: that layer IS the input-latency-critical one and it is
// cleared and redrawn every frame anyway.
function renderContext2dOptions(opaque = false, desynchronized = true, willReadFrequently = false) {
  // The powerups canvas is the bottom layer over a black backdrop, so it can
  // be opaque: one less full-screen alpha blend in every composite.
  return { alpha: !opaque, desynchronized, willReadFrequently };
}

// ONE VISIBLE CANVAS, on the devices that need it.
//
// On Chrome and Brave for Android the trail did not appear, while Firefox and
// the Android WebView on the same phone were fine, and opening any modal made
// the whole trail show at once. The deciding test was lifting the trail above
// the heads canvas: the trail drew and the heads and powerups vanished. On that
// device only the TOPMOST canvas of a stack is presented -- nothing was wrong
// with the drawing, the bitmap, the z-order or the context options.
//
// So merged mode stops asking the browser to stack any. Powerups, trail and
// heads keep their own canvases and their own draw code; they are simply
// offscreen, and every frame they are blitted bottom-to-top onto the single
// canvas that is in the page. Compositing order becomes this file's decision
// rather than the compositor's.
//
// It is not free -- three full-surface blits a frame, and a headless run with
// software rasterization went from 25 frames over 33 ms in ten seconds to 165 --
// so it is the default only where stacking is known to break. That is a NARROWER
// test than the canvas budget above, on purpose: the budget errs towards being
// applied (getting it wrong costs a desktop some sharpness), while a wrongly
// merged desktop pays for those blits every frame. So only the two direct
// touch-device signals count here, not the memory or screen-size fallbacks.
//
// `?arena=merge` and `?arena=stack` force either, which is how the two get
// compared on one device.
const ARENA_MODE = (() => {
  let forced = '';
  try {
    const value = new URLSearchParams(window.location.search).get('arena');
    if (value === 'merge' || value === 'stack') forced = value;
  } catch (_) {
    forced = '';
  }
  if (forced) return forced;
  try {
    const touch = window.matchMedia('(pointer: coarse)').matches
      || window.matchMedia('(hover: none)').matches;
    return touch ? 'merge' : 'stack';
  } catch (_) {
    return 'stack';
  }
})();
const ARENA_MERGED = ARENA_MODE === 'merge';

function compositeArenaFrame() {
  if (!ARENA_MERGED) return;
  if (!ensureTrailContext()) return;
  const target = trailCtx;
  const width = Math.max(1, Math.floor(Number(renderSurface.backingWidth) || 1));
  const height = Math.max(1, Math.floor(Number(renderSurface.backingHeight) || 1));
  target.save();
  if (typeof target.setTransform === 'function') target.setTransform(1, 0, 0, 1, 0, 0);
  target.clearRect(0, 0, width, height);
  if (powerupsCanvas) target.drawImage(powerupsCanvas, 0, 0, width, height);
  for (const surface of trailSurfacesByPercent.values()) {
    if (!surface?.canvas || surface.canvas === trailCanvas) continue;
    target.globalAlpha = surface.percent / 100;
    target.drawImage(surface.canvas, 0, 0, width, height);
  }
  target.globalAlpha = 1;
  if (headsCanvas) target.drawImage(headsCanvas, 0, 0, width, height);
  target.restore();
}

// Merged mode's layers are hidden rather than detached: they stay in the
// document so the stage fit keeps sizing them, they just never paint to the
// screen themselves. Called every frame because the trail grows canvases at
// runtime, so it tracks what it has already hidden and does nothing otherwise.
let appliedMergedLayerCount = -1;

function applyArenaLayerVisibility() {
  if (!ARENA_MERGED) return;
  const canvases = trailSurfaceCanvases();
  if (canvases.length === appliedMergedLayerCount) return;
  appliedMergedLayerCount = canvases.length;
  if (powerupsCanvas) powerupsCanvas.style.visibility = 'hidden';
  if (headsCanvas) headsCanvas.style.visibility = 'hidden';
  for (const canvas of canvases) {
    canvas.style.visibility = canvas === trailCanvas ? 'visible' : 'hidden';
    if (canvas === trailCanvas) canvas.style.opacity = '1';
  }
}

function applyCanvasAntialias(ctx) {
  if (!ctx) return;
  if (typeof ctx.imageSmoothingEnabled === 'boolean') {
    ctx.imageSmoothingEnabled = ENABLE_CANVAS_ANTIALIAS;
  }
  if ('imageSmoothingQuality' in ctx) {
    ctx.imageSmoothingQuality = ENABLE_CANVAS_ANTIALIAS ? 'high' : 'low';
  }
}

function applyCanvasTransform(ctx) {
  if (!ctx) return;
  if (typeof ctx.setTransform === 'function') {
    ctx.setTransform(renderSurface.scaleX, 0, 0, renderSurface.scaleY, 0, 0);
  }
  applyCanvasAntialias(ctx);
}

function clearCanvasContext(ctx) {
  if (!ctx?.canvas) return;
  ctx.save();
  if (typeof ctx.setTransform === 'function') {
    ctx.setTransform(1, 0, 0, 1, 0, 0);
  }
  ctx.clearRect(0, 0, ctx.canvas.width, ctx.canvas.height);
  ctx.restore();
}

function markPowerupsDirty() {
  powerupRenderCache.dirty = true;
}

function cachedCirclePath(radius) {
  if (typeof Path2D !== 'function') return null;
  const r = Math.max(0.1, Number(radius) || 0);
  const key = Math.round(r * 100) / 100;
  const cached = circlePathCache.get(key);
  if (cached) return cached;
  const path = new Path2D();
  path.arc(0, 0, r, 0, Math.PI * 2);
  circlePathCache.set(key, path);
  return path;
}

function fillCenteredCircle(ctx, radius, color) {
  ctx.fillStyle = color;
  const path = cachedCirclePath(radius);
  if (path) {
    ctx.fill(path);
    return;
  }
  ctx.beginPath();
  ctx.arc(0, 0, radius, 0, Math.PI * 2);
  ctx.fill();
}

function easeOutCubic(t) {
  const x = clamp(t, 0, 1);
  return 1 - Math.pow(1 - x, 3);
}

function easeOutBack(t) {
  const x = clamp(t, 0, 1);
  const c1 = 1.70158;
  const c3 = c1 + 1;
  return 1 + c3 * Math.pow(x - 1, 3) + c1 * Math.pow(x - 1, 2);
}

function gemEffectColor(colors, index, fallback) {
  const candidate = Array.isArray(colors) ? colors[index] : '';
  return typeof candidate === 'string' && candidate.trim() ? candidate : fallback;
}

function gemEffectSeed(powerup, index) {
  const id = Number(powerup?.id);
  const variantValue = Number(powerup?.value);
  return (
    (Number.isFinite(id) ? Math.floor(id) * 37 : 0)
    + (Number.isFinite(variantValue) ? Math.floor(variantValue) * 53 : 0)
    + index * 17
  ) % 360;
}

function queueGemCollectionEffects(powerups, startAtMs = Date.now()) {
  const items = Array.isArray(powerups) ? powerups : [];
  const atMs = Number.isFinite(Number(startAtMs)) ? Number(startAtMs) : Date.now();
  for (let i = 0; i < items.length; i += 1) {
    const powerup = items[i] || {};
    const x = Number(powerup.x);
    const y = Number(powerup.y);
    const value = Math.max(0, Math.floor(Number(powerup.value) || 0));
    if (powerup.type !== PU_TYPES.GEM || !Number.isFinite(x) || !Number.isFinite(y) || value <= 0) continue;
    if (gemCollectionEffects.length >= GEM_COLLECTION_EFFECT_MAX_ACTIVE) {
      gemCollectionEffects.splice(0, gemCollectionEffects.length - GEM_COLLECTION_EFFECT_MAX_ACTIVE + 1);
    }
    gemCollectionEffects.push({
      x,
      y,
      value,
      label: `+${value}`,
      startAtMs: atMs,
      seed: gemEffectSeed(powerup, i),
      color: gemEffectColor(powerup.colors, 0, '#ff8a1f'),
      glow: gemEffectColor(powerup.colors, 1, '#ffc074'),
      shade: gemEffectColor(powerup.colors, 2, '#8f3608')
    });
  }
}

function getCanvas2dContext(canvas, { opaque = false, desynchronized = true, readFrequently = false } = {}) {
  if (!canvas || typeof canvas.getContext !== 'function') return null;
  let ctx = null;
  try {
    ctx = canvas.getContext('2d', renderContext2dOptions(opaque, desynchronized, readFrequently));
  } catch (_) {
    ctx = null;
  }
  if (!ctx) {
    ctx = canvas.getContext('2d');
  }
  applyCanvasTransform(ctx);
  return ctx;
}

// Retained trail layers hold pixels, so a layer coarser than the backing store
// is a resolution ceiling on every composite: the trail is redrawn from the
// layers after an erase or a resize, and at field resolution that visibly
// softens it on any display larger than the 720 px field. They are per player,
// though, so their cost is multiplied by the room size -- hence a budget rather
// than an unconditional match. It only binds above a 2000 px arena, where the
// residual upscale is a fraction of a pixel.
// Same budget, harder limit: these are per PLAYER, so an eight-player room
// multiplies whatever this says by eight. A layer is only ever blitted back
// after a death, so a smaller one costs a little softness at that one moment --
// worth paying on a phone, where 1.2M keeps a full room's retained pixels
// inside ~40MB rather than 120, and not worth paying on a desktop.
const MAX_TRAIL_LAYER_PIXELS = LOW_CANVAS_BUDGET ? 1_200_000 : 4_000_000;

function trailLayerSize() {
  const backingWidth = Math.max(1, Math.floor(Number(renderSurface.backingWidth) || 1));
  const backingHeight = Math.max(1, Math.floor(Number(renderSurface.backingHeight) || 1));
  const pixels = backingWidth * backingHeight;
  const scale = pixels > MAX_TRAIL_LAYER_PIXELS
    ? Math.sqrt(MAX_TRAIL_LAYER_PIXELS / pixels)
    : 1;
  return {
    width: Math.max(1, Math.round(backingWidth * scale)),
    height: Math.max(1, Math.round(backingHeight * scale))
  };
}

function createTrailLayerSurface() {
  const fieldWidth = Math.max(1, Number(renderSurface.fieldWidth) || DEFAULT_CONFIG.fieldWidth);
  const fieldHeight = Math.max(1, Number(renderSurface.fieldHeight) || DEFAULT_CONFIG.fieldHeight);
  const { width, height } = trailLayerSize();
  // A detached <canvas>, not an OffscreenCanvas. Both hold pixels and both can
  // be drawImage'd, but they are separate implementations in the browser, and
  // Chrome on Android is where trails went missing while Firefox on the same
  // phone was fine -- these layers are what the visible trail is rebuilt from
  // after every death, so if a blit from one of them does nothing, the trail
  // does not come back. OffscreenCanvas remains the fallback, since the older
  // path is better than none.
  let canvas = null;
  try {
    if (typeof document !== 'undefined' && typeof document.createElement === 'function') {
      canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
    } else if (typeof OffscreenCanvas === 'function') {
      canvas = new OffscreenCanvas(width, height);
    }
  } catch (_) {
    canvas = null;
  }
  if (!canvas) return null;

  let ctx = null;
  try {
    // Retained pixels, so never desynchronized -- and meaningless offscreen.
    ctx = canvas.getContext('2d', renderContext2dOptions(false, false));
  } catch (_) {
    ctx = null;
  }
  if (!ctx) {
    try {
      ctx = canvas.getContext('2d');
    } catch (_) {
      ctx = null;
    }
  }
  if (!ctx) return null;
  applyCanvasAntialias(ctx);
  // Trail ops are in field coordinates, so the layer carries its own
  // field -> layer-pixel transform rather than the main surface's.
  if (typeof ctx.setTransform === 'function') {
    ctx.setTransform(width / fieldWidth, 0, 0, height / fieldHeight, 0, 0);
  }
  return { canvas, ctx };
}

// A backing-store change (window resize, DPR change, resolution setting) leaves
// the layers at the old size. Carry their pixels across rather than dropping a
// round's worth of trail; new segments then draw at the new resolution.
function resizeTrailOwnerLayers() {
  if (trailLayersByOwner.size === 0) return;
  const { width, height } = trailLayerSize();
  for (const [ownerId, layer] of [...trailLayersByOwner]) {
    if (!layer?.canvas) continue;
    if (layer.canvas.width === width && layer.canvas.height === height) continue;
    const next = createTrailLayerSurface();
    if (!next) {
      trailLayersByOwner.delete(ownerId);
      continue;
    }
    next.ctx.save();
    next.ctx.setTransform(1, 0, 0, 1, 0, 0);
    next.ctx.drawImage(layer.canvas, 0, 0, next.canvas.width, next.canvas.height);
    next.ctx.restore();
    trailLayersByOwner.set(ownerId, next);
  }
}

function ensureTrailOwnerLayer(ownerId) {
  const key = ownerId === null ? -1 : ownerId;
  const existing = trailLayersByOwner.get(key);
  if (existing) return existing;
  const layer = createTrailLayerSurface();
  if (!layer) return null;
  trailLayersByOwner.set(key, layer);
  return layer;
}

function clearTrailLayerState() {
  trailDiagCounts.clears += 1;
  for (const layer of trailLayersByOwner.values()) {
    if (!layer?.ctx || !layer?.canvas) continue;
    clearCanvasContext(layer.ctx);
  }
  trailAnchors.clear();
  trailCompositeDirty = false;
  if (ensureTrailContext()) {
    forEachTrailSurfaceCtx((ctx) => clearCanvasContext(ctx));
  }
}

function compositeTrailOwnerLayers() {
  if (!ensureTrailContext()) return false;
  // Blit in device pixels: a layer at the backing size then lands 1:1 instead
  // of being resampled through the field-coordinate transform.
  const backingWidth = Math.max(1, Math.floor(Number(renderSurface.backingWidth) || 1));
  const backingHeight = Math.max(1, Math.floor(Number(renderSurface.backingHeight) || 1));
  trailDiagCounts.composites += 1;
  forEachTrailSurfaceCtx((ctx) => clearCanvasContext(ctx));
  for (const [ownerId, layer] of trailLayersByOwner) {
    if (!layer?.canvas) continue;
    // Each owner lands on the canvas for ITS alpha, which is what makes a
    // change of alpha a re-composite rather than a redraw of the round.
    const ctx = trailCtxForOwner(ownerId < 0 ? null : ownerId);
    if (!ctx) continue;
    ctx.save();
    if (typeof ctx.setTransform === 'function') {
      ctx.setTransform(1, 0, 0, 1, 0, 0);
    }
    ctx.drawImage(layer.canvas, 0, 0, backingWidth, backingHeight);
    ctx.restore();
  }
  trailCompositeDirty = false;
  return true;
}

function drawTrailOpRun(ops) {
  if (!Array.isArray(ops) || ops.length === 0) return;

  const opsByOwner = new Map();
  for (const op of ops) {
    if (!op || (op.k !== 's' && op.k !== 'p')) continue;
    const ownerId = trailOpOwnerId(op);
    const key = ownerId === null ? -1 : ownerId;
    let ownerOps = opsByOwner.get(key);
    if (!ownerOps) {
      ownerOps = [];
      opsByOwner.set(key, ownerOps);
    }
    ownerOps.push(op);
  }
  for (const [ownerId, ownerOps] of opsByOwner) {
    // The visible canvas for this owner's alpha, drawn at full backing
    // resolution -- the retained layer is a copy for erase and re-composite,
    // never the thing on screen.
    const percent = ownerId < 0 ? 100 : slotTrailOpacityPercent(ownerId);
    const surface = trailSurfaceForPercent(percent);
    const ctx = trailSurfaceCtx(surface);
    if (ctx) applyTrailOpsCore(ctx, ownerOps);
    trailDiagCounts.ops += ownerOps.length;
    noteTrailOpBounds(ownerOps);
    const layer = ensureTrailOwnerLayer(ownerId < 0 ? null : ownerId);
    if (!layer) continue;
    applyTrailOpsCore(layer.ctx, ownerOps);
  }
  recordTrailAnchors(ops);
}

function eraseTrailOwnerLayer(op) {
  trailDiagCounts.erases += 1;
  const ownerId = trailOpOwnerId(op);
  if (ownerId === null) return;
  const layer = trailLayersByOwner.get(ownerId);
  if (layer?.ctx && layer?.canvas) {
    clearCanvasContext(layer.ctx);
  }
  trailAnchors.delete(ownerId);
  compositeTrailOwnerLayers();
}

// The canvases carry an inline visibility so they survive the game layout
// going offstage (see .game-layout.is-offstage), which is why this is set
// explicitly rather than left to the stylesheet.
function showRenderCanvases() {
  for (const canvas of trailSurfaceCanvases()) {
    canvas.style.visibility = 'visible';
  }
  appliedTrailOpacitySignature = null;
  syncTrailOpacityStyle();
  headsCanvas.style.visibility = 'visible';
  powerupsCanvas.style.visibility = 'visible';
  // That just un-hid the offscreen layers merged mode relies on being hidden,
  // so it has to re-decide rather than trust what it applied last.
  appliedMergedLayerCount = -1;
  applyArenaLayerVisibility();
}

function currentRenderSurfaces() {
  return {
    trail: trailCanvas,
    heads: headsCanvas,
    powerups: powerupsCanvas
  };
}

function ensureMainRenderContexts() {
  ensureTrailContext();
  ensureHeadsContext();
  ensurePowerupsContext();
  return Boolean(trailCtx && headsCtx && powerupsCtx);
}

function ensurePowerupsContext() {
  if (powerupsCtx) return true;
  const surfaces = currentRenderSurfaces();
  // NOT opaque, and not desynchronized. Both were tried as fixes for the
  // Android trail bug on the theory that an opaque low-latency canvas is what
  // gets promoted to a hardware overlay, and neither fixed it -- the cause was
  // the canvas stack itself (see ARENA_MERGED).
  //
  // They stay off because they buy nothing here. The black backdrop comes from
  // `#powerups { background: #000 }` in the stylesheet rather than the bitmap,
  // so an opaque context saves one full-screen alpha blend and risks the
  // overlay path for it; and this layer redraws on demand rather than every
  // frame, so it has no input latency to save. In merged mode it is offscreen
  // and neither hint means anything at all.
  powerupsCtx = getCanvas2dContext(surfaces.powerups, { opaque: false, desynchronized: false });
  return Boolean(powerupsCtx);
}

function ensureTrailContext() {
  if (trailCtx) return true;
  const surfaces = currentRenderSurfaces();
  // Merged mode redraws this one whole every frame, so it could take the
  // low-latency hint -- but it is also the surface the stacked path accumulates
  // into, and one context serves both.
  trailCtx = getCanvas2dContext(surfaces.trail, { desynchronized: false });
  return Boolean(trailCtx);
}

function ensureHeadsContext() {
  if (headsCtx) return true;
  const surfaces = currentRenderSurfaces();
  headsCtx = getCanvas2dContext(surfaces.heads);
  return Boolean(headsCtx);
}



async function setupRenderBackend() {
  showRenderCanvases();
  ensureMainRenderContexts();
}


function setCanvasSize(width, height) {
  const nextWidth = Math.max(1, Math.floor(Number(width) || 0));
  const nextHeight = Math.max(1, Math.floor(Number(height) || 0));

  if (arenaEl && arenaEl.style && typeof arenaEl.style.setProperty === 'function') {
    arenaEl.style.setProperty('--arena-base-width', `${nextWidth}px`);
    arenaEl.style.setProperty('--arena-base-height', `${nextHeight}px`);
    // The field's shape, which is what decides the arena's shape on screen. A
    // crowded room plays on a WIDER field (see fieldSizeForPlayers), and the
    // arena is height-bound, so the extra width has to come out of the space
    // sitting empty beside a square arena rather than out of its height.
    const aspect = clamp(nextWidth / nextHeight, 0.5, 3);
    arenaEl.style.setProperty('--arena-aspect', String(aspect));
  }

  applyRenderSurface(computeRenderSurface(
    renderSurface.cssWidth || nextWidth,
    renderSurface.cssHeight || nextHeight,
    nextWidth,
    nextHeight
  ));
}

function renderQualityPixelRatio() {
  const deviceRatio = typeof window !== 'undefined'
    ? Number(window.devicePixelRatio) || 1
    : 1;
  const quality = clamp((Number(settings.resolutionPercent) || 100) / 100, 0.5, 1);
  return clamp(deviceRatio * quality, 0.5, MAX_RENDER_PIXEL_RATIO);
}

function computeRenderSurface(cssWidth, cssHeight, fieldWidth, fieldHeight) {
  const width = Math.max(1, Number(cssWidth) || fieldWidth);
  const height = Math.max(1, Number(cssHeight) || fieldHeight);
  let pixelRatio = renderQualityPixelRatio();
  const requestedPixels = width * height * pixelRatio * pixelRatio;
  if (requestedPixels > MAX_RENDER_SURFACE_PIXELS) {
    pixelRatio *= Math.sqrt(MAX_RENDER_SURFACE_PIXELS / requestedPixels);
  }
  pixelRatio = clamp(pixelRatio, 0.5, MAX_RENDER_PIXEL_RATIO);

  const backingWidth = Math.max(1, Math.round(width * pixelRatio));
  const backingHeight = Math.max(1, Math.round(height * pixelRatio));
  return {
    fieldWidth,
    fieldHeight,
    cssWidth: Math.max(1, Math.round(width)),
    cssHeight: Math.max(1, Math.round(height)),
    backingWidth,
    backingHeight,
    scaleX: backingWidth / fieldWidth,
    scaleY: backingHeight / fieldHeight,
    pixelRatio
  };
}

function resizeLayerBacking(layer, width, height) {
  if (!layer) return false;
  const nextWidth = Math.max(1, Math.floor(Number(width) || 1));
  const nextHeight = Math.max(1, Math.floor(Number(height) || 1));
  const changed = layer.width !== nextWidth || layer.height !== nextHeight;
  if (changed) {
    layer.width = nextWidth;
    layer.height = nextHeight;
  }
  return changed;
}

function applyRenderSurface(nextSurface) {
  if (!nextSurface) return;
  const fieldSizeChanged = renderSurface.fieldWidth !== nextSurface.fieldWidth
    || renderSurface.fieldHeight !== nextSurface.fieldHeight;
  const changed = renderSurface.fieldWidth !== nextSurface.fieldWidth
    || renderSurface.fieldHeight !== nextSurface.fieldHeight
    || renderSurface.backingWidth !== nextSurface.backingWidth
    || renderSurface.backingHeight !== nextSurface.backingHeight
    || Math.abs(renderSurface.scaleX - nextSurface.scaleX) > 0.0001
    || Math.abs(renderSurface.scaleY - nextSurface.scaleY) > 0.0001;

  Object.assign(renderSurface, nextSurface);
  if (changed) {
    markPowerupsDirty();
    trailAnchors.clear();
    if (fieldSizeChanged) {
      trailLayersByOwner.clear();
      trailCompositeDirty = false;
    } else if (trailLayersByOwner.size > 0) {
      resizeTrailOwnerLayers();
      trailCompositeDirty = true;
    }
    previousHeadDirtyRects = [];
    previousHeadFrameWasComplex = false;
    headsNeedFullClear = true;
  }

  for (const surface of trailSurfacesByPercent.values()) {
    if (resizeLayerBacking(surface.canvas, renderSurface.backingWidth, renderSurface.backingHeight)) {
      surface.ctx = null;
      if (surface.canvas === trailCanvas) trailCtx = null;
    }
  }
  if (resizeLayerBacking(trailCanvas, renderSurface.backingWidth, renderSurface.backingHeight)) {
    trailCtx = null;
  }
  if (resizeLayerBacking(headsCanvas, renderSurface.backingWidth, renderSurface.backingHeight)) {
    headsCtx = null;
  }
  if (resizeLayerBacking(powerupsCanvas, renderSurface.backingWidth, renderSurface.backingHeight)) {
    powerupsCtx = null;
    markPowerupsDirty();
  }

  for (const surface of trailSurfacesByPercent.values()) {
    if (surface.ctx) applyCanvasTransform(surface.ctx);
  }
  if (trailCtx) applyCanvasTransform(trailCtx);
  if (headsCtx) applyCanvasTransform(headsCtx);
  if (powerupsCtx) applyCanvasTransform(powerupsCtx);
}



function fitStageToViewport() {
  const anchorEl = arenaFrameEl || arenaEl;
  if (!anchorEl) return;

  // Clear previous inline positioning/sizing so measurement is based on layout, not prior fit output.
  for (const layer of canvasLayers()) {
    layer.style.left = '';
    layer.style.top = '';
    layer.style.width = '';
    layer.style.height = '';
  }

  if (arenaFrameEl) {
    arenaFrameEl.style.left = '';
    arenaFrameEl.style.top = '';
    arenaFrameEl.style.width = '';
    arenaFrameEl.style.height = '';
  }

  if (focusOverlayEl) {
    focusOverlayEl.style.left = '';
    focusOverlayEl.style.top = '';
    focusOverlayEl.style.width = '';
    focusOverlayEl.style.height = '';
  }

  if (roundOverlayEl) {
    roundOverlayEl.style.left = '';
    roundOverlayEl.style.top = '';
    roundOverlayEl.style.width = '';
    roundOverlayEl.style.maxWidth = '';
    roundOverlayEl.style.maxHeight = '';
  }

  const rect = anchorEl.getBoundingClientRect();
  const fieldWidth = Math.max(1, Number(model.config.fieldWidth) || DEFAULT_CONFIG.fieldWidth);
  const fieldHeight = Math.max(1, Number(model.config.fieldHeight) || DEFAULT_CONFIG.fieldHeight);
  const width = rect.width > 0 ? rect.width : fieldWidth;
  const height = rect.height > 0 ? rect.height : fieldHeight;
  const fieldAspect = fieldWidth / fieldHeight;

  let mapWidth = width;
  let mapHeight = Math.floor(mapWidth / fieldAspect);
  if (mapHeight > height) {
    mapHeight = height;
    mapWidth = Math.floor(mapHeight * fieldAspect);
  }

  mapWidth = Math.max(1, Math.floor(mapWidth));
  mapHeight = Math.max(1, Math.floor(mapHeight));

  const mapLeft = Math.floor((width - mapWidth) / 2);
  const mapTop = Math.floor((height - mapHeight) / 2);

  for (const layer of canvasLayers()) {
    layer.style.left = `${mapLeft}px`;
    layer.style.top = `${mapTop}px`;
    layer.style.width = `${mapWidth}px`;
    layer.style.height = `${mapHeight}px`;
  }

  applyRenderSurface(computeRenderSurface(mapWidth, mapHeight, fieldWidth, fieldHeight));

  if (arenaFrameEl) {
    arenaFrameEl.style.left = `${mapLeft}px`;
    arenaFrameEl.style.top = `${mapTop}px`;
    arenaFrameEl.style.width = `${mapWidth}px`;
    arenaFrameEl.style.height = `${mapHeight}px`;
  }

  // The click-to-focus band belongs to the playfield, not the grid cell the
  // playfield is letterboxed inside, so it takes the same box as the canvases.
  if (focusOverlayEl) {
    focusOverlayEl.style.left = `${mapLeft}px`;
    focusOverlayEl.style.top = `${mapTop}px`;
    focusOverlayEl.style.width = `${mapWidth}px`;
    focusOverlayEl.style.height = `${mapHeight}px`;
  }

  if (roundOverlayEl) {
    roundOverlayEl.style.left = `${mapLeft + Math.floor(mapWidth / 2)}px`;
    roundOverlayEl.style.top = `${mapTop + Math.floor(mapHeight / 2)}px`;
    roundOverlayEl.style.width = `${mapWidth}px`;
    roundOverlayEl.style.maxWidth = `${mapWidth}px`;
    roundOverlayEl.style.maxHeight = `${mapHeight}px`;
  }
}

// A round start clears the field, and it used to DROP every retained trail
// layer, so the first stroke of the new round had to allocate one canvas per
// player again. At a 1612x1612 backing store one of those costs 13-22ms to
// allocate and clear, so an eight-player room spent ~125ms of the opening
// rebuilding surfaces it had just thrown away -- measured as a 100ms frame and
// a run of dropped ones right where the round begins. The cost scales with the
// roster, which is why it shows up in a full room and not in a 1v1.
//
// The pixels are what has to go, not the canvases. Clearing them keeps every
// layer at the size and transform it already had; a layer belonging to a player
// who has left simply stays empty until it is reused. Backing-store changes are
// handled separately (resizeTrailOwnerLayers), and a field-size change still
// drops them, so nothing here can outlive a size it no longer matches.
function clearField() {
  pendingTrailOps.length = 0;
  for (const layer of trailLayersByOwner.values()) {
    if (layer?.ctx) clearCanvasContext(layer.ctx);
  }
  trailCompositeDirty = false;
  trailAnchors.clear();
  previousHeadDirtyRects = [];
  previousHeadFrameWasComplex = false;
  headsNeedFullClear = true;
  if (ensureTrailContext()) {
    forEachTrailSurfaceCtx((ctx) => clearCanvasContext(ctx));
  }
  if (ensureHeadsContext()) {
    clearCanvasContext(headsCtx);
  }
  if (ensurePowerupsContext()) {
    clearCanvasContext(powerupsCtx);
  }
  markPowerupsDirty();
}

function trailOpOwnerId(op) {
  const owner = Number(op?.o);
  if (!Number.isFinite(owner) || owner < 0) return null;
  return Math.floor(owner);
}

function recordTrailAnchorFromOp(op) {
  const ownerId = trailOpOwnerId(op);
  if (ownerId === null) return;
  if (op?.k === 's') {
    const x = Number(op.x2);
    const y = Number(op.y2);
    const r = Number(op.r);
    if (!Number.isFinite(x) || !Number.isFinite(y) || !Number.isFinite(r) || r <= 0) return;
    trailAnchors.set(ownerId, {
      x,
      y,
      r,
      c: typeof op.c === 'string' && op.c ? op.c : '#ffffff'
    });
    return;
  }
  if (op?.k === 'p') {
    const x = Number(op.x);
    const y = Number(op.y);
    const r = Number(op.r);
    if (!Number.isFinite(x) || !Number.isFinite(y) || !Number.isFinite(r) || r <= 0) return;
    trailAnchors.set(ownerId, {
      x,
      y,
      r,
      c: typeof op.c === 'string' && op.c ? op.c : '#ffffff'
    });
  }
}

function recordTrailAnchors(ops) {
  if (!Array.isArray(ops)) return;
  for (const op of ops) {
    if (!op || typeof op !== 'object') continue;
    if (op.k === 'c') {
      trailAnchors.clear();
      continue;
    }
    if (op.k === 'r') {
      const ownerId = trailOpOwnerId(op);
      if (ownerId !== null) trailAnchors.delete(ownerId);
      continue;
    }
    recordTrailAnchorFromOp(op);
  }
}

function flushTrailOps() {
  if (pendingTrailOps.length === 0) {
    if (trailCompositeDirty) compositeTrailOwnerLayers();
    return;
  }
  const ops = pendingTrailOps.splice(0, pendingTrailOps.length);

  if (!ensureTrailContext()) {
    pendingTrailOps.unshift(...ops);
    return;
  }
  if (trailCompositeDirty) compositeTrailOwnerLayers();

  const run = [];
  const flushRun = () => {
    if (run.length === 0) return;
    drawTrailOpRun(run);
    run.length = 0;
  };
  for (const op of ops) {
    if (!op || typeof op !== 'object') continue;
    if (op.k === 'c') {
      flushRun();
      clearTrailLayerState();
      continue;
    }
    if (op.k === 'r') {
      flushRun();
      eraseTrailOwnerLayer(op);
      continue;
    }
    if (op.k === 's' || op.k === 'p') {
      run.push(op);
    }
  }
  flushRun();
}

function syncRenderPrefs() {
  applyRenderSurface(computeRenderSurface(
    renderSurface.cssWidth,
    renderSurface.cssHeight,
    Number(model.config.fieldWidth) || DEFAULT_CONFIG.fieldWidth,
    Number(model.config.fieldHeight) || DEFAULT_CONFIG.fieldHeight
  ));
  applyCanvasTransform(trailCtx);
  applyCanvasTransform(headsCtx);
  applyCanvasTransform(powerupsCtx);
  markPowerupsDirty();
}

function applyOps(ops) {
  if (!Array.isArray(ops) || ops.length === 0) return;
  pendingTrailOps.push(...ops);
}

function getPowerupIcon(src) {
  if (!src) return null;
  const cached = powerupIconCache.get(src);
  if (cached) return cached;
  const image = new Image();
  image.decoding = 'async';
  image.onload = markPowerupsDirty;
  image.src = src;
  powerupIconCache.set(src, image);
  return image;
}

const PU_STYLE_COLORS = {
  SPEED: '#5a9e30',
  CLEAR: '#3060a0',
  GEM: '#ff8a1f',
  GEM_RED: '#e34f38',
  GEM_PURPLE: '#a855f7'
};

function powerupStyleColor(powerup) {
  const type = powerup?.type || 'SPEED';
  const colors = Array.isArray(powerup?.colors) ? powerup.colors : [];
  const primary = colors.find((color) => typeof color === 'string' && color.trim());
  if (primary) return primary;
  return PU_STYLE_COLORS[type] || '#ccc';
}

function isGemIcon(powerup) {
  if (powerup?.type === PU_TYPES.GEM) return true;
  const iconSrc = typeof powerup?.iconSrc === 'string' ? powerup.iconSrc : '';
  return iconSrc.endsWith('/gem.svg') || iconSrc.endsWith('gem.svg');
}

function powerupIconMetrics(powerup, radius) {
  if (isGemIcon(powerup)) {
    return {
      size: radius * GEM_ICON_SCALE,
      offsetX: 0,
      offsetY: radius * GEM_ICON_CENTER_Y_OFFSET
    };
  }
  return {
    size: radius * POWERUP_ICON_SCALE,
    offsetX: 0,
    offsetY: 0
  };
}

function powerupRenderSignature(powerups, radius) {
  const version = Number(model.state?.powerupsVersion);
  if (Number.isFinite(version)) {
    return `${Math.round(radius * 100)}:${Math.floor(version)}`;
  }

  let signature = `${powerups.length}:${Math.round(radius * 100)}`;
  for (let i = 0; i < powerups.length; i += 1) {
    const powerup = powerups[i] || {};
    const id = Number.isFinite(Number(powerup.id)) ? Math.floor(Number(powerup.id)) : 0;
    const x = Number.isFinite(Number(powerup.x)) ? Math.round(Number(powerup.x) * 10) : 0;
    const y = Number.isFinite(Number(powerup.y)) ? Math.round(Number(powerup.y) * 10) : 0;
    const spawnTick = Number.isFinite(Number(powerup.spawnTick)) ? Math.floor(Number(powerup.spawnTick)) : 0;
    const type = typeof powerup.type === 'string' ? powerup.type : '';
    const variant = typeof powerup.variant === 'string' ? powerup.variant : '';
    const iconSrc = typeof powerup.iconSrc === 'string' ? powerup.iconSrc : '';
    signature += `|${id}:${x}:${y}:${spawnTick}:${type}:${variant}:${powerupStyleColor(powerup)}:${iconSrc}`;
  }
  return signature;
}

function powerupAgeMs(tickNow, spawnTick, tickMs) {
  return Math.max(0, (tickNow - spawnTick) * tickMs);
}

function powerupsHaveActiveAnimation(powerups, tickNow, tickMs) {
  for (let i = 0; i < powerups.length; i += 1) {
    const spawnTick = Number(powerups[i]?.spawnTick);
    if (!Number.isFinite(spawnTick)) continue;
    const ageMs = powerupAgeMs(tickNow, spawnTick, tickMs);
    if (ageMs < POWERUP_SPAWN_ANIM_MS) return true;
  }
  return false;
}

function drawPowerup(powerup, tickNow, tickMs, radius) {
  const x = Number(powerup?.x);
  const y = Number(powerup?.y);
  if (!Number.isFinite(x) || !Number.isFinite(y)) return;

  const color = powerupStyleColor(powerup);

  // Animation: Spawn Scale (BackOut easing)
  // Matching curvereloaded: age < 750ms
  const spawnTick = Number(powerup?.spawnTick) || 0;
  const ageMs = powerupAgeMs(tickNow, spawnTick, tickMs);

  let animScale = 1;
  const animDuration = POWERUP_SPAWN_ANIM_MS;
  if (ageMs < animDuration) {
    const t = ageMs / animDuration;
    const c1 = 1.70158;
    const c3 = c1 + 1;
    animScale = 1 + c3 * Math.pow(t - 1, 3) + c1 * Math.pow(t - 1, 2);
    if (animScale < 0) animScale = 0;
  }

  powerupsCtx.save();
  powerupsCtx.translate(x, y);
  powerupsCtx.scale(animScale, animScale);

  fillCenteredCircle(powerupsCtx, radius, color);

  if (powerup?.iconSrc) {
    const image = getPowerupIcon(powerup.iconSrc);
    if (image && image.complete && image.naturalHeight !== 0) {
      const metrics = powerupIconMetrics(powerup, radius);
      powerupsCtx.drawImage(
        image,
        metrics.offsetX - metrics.size / 2,
        metrics.offsetY - metrics.size / 2,
        metrics.size,
        metrics.size
      );
    }
  }

  powerupsCtx.restore();
}

function drawPowerups(nowMs = Date.now()) {
  if (!ensurePowerupsContext()) return;
  const powerups = Array.isArray(model.state.powerups) ? model.state.powerups : [];
  const tickNow = stateTickNow(nowMs);
  const tickMs = Math.max(1, Number(model.config.tickMs) || DEFAULT_CONFIG.tickMs);
  const radius = Number(model.config.powerupRadius) || POWERUP_RADIUS;
  const signature = powerupRenderSignature(powerups, radius);
  const animating = powerupsHaveActiveAnimation(powerups, tickNow, tickMs);
  if (
    !powerupRenderCache.dirty
    && powerupRenderCache.signature === signature
    && !animating
    && !powerupRenderCache.wasAnimating
  ) {
    return;
  }

  clearCanvasContext(powerupsCtx);
  for (const powerup of powerups) {
    drawPowerup(powerup, tickNow, tickMs, radius);
  }
  powerupRenderCache.signature = signature;
  powerupRenderCache.dirty = false;
  powerupRenderCache.wasAnimating = animating;
}

// Whose head is whose. Colour cannot carry it: every player picks their own and
// the trail is already read by colour, so two team-mates on neighbouring hues
// would be indistinguishable.
//
// The SQUARE MEANS OPPOSITION -- your own side keeps the round head, so the
// shape answers the question you actually have mid-match ("can I cut across
// this one") instead of making you remember which side you are first. Both
// teams see squares, each on the other.
//
// Cosmetic only: collision is a disc of headRadius whatever is drawn here.
function localPlayerTeam() {
  const players = model.state?.players;
  if (!Array.isArray(players)) return null;
  const localId = Math.floor(Number(net.localPlayerId));
  if (!Number.isFinite(localId)) return null;
  const me = players.find((player) => Math.floor(Number(player?.id)) === localId);
  return normalizeTeam(me?.team);
}

// A read-out for a device we cannot attach a debugger to. `?diag=trail` puts the
// numbers that matter on the screen -- what the trail canvas is, whether it has
// pixels, whether the retained layers have pixels, and how often the code that
// rebuilds one from the other has run. Off unless asked for.
const TRAIL_DIAG = (() => {
  try {
    return new URLSearchParams(window.location.search).get('diag') === 'trail';
  } catch (_) {
    return false;
  }
})();
const trailDiagCounts = { ops: 0, composites: 0, clears: 0, erases: 0 };
// Where the last run of ops was asked to draw, in field units, plus the widest
// stroke in it. If the numbers here are sane but nothing is painted, the
// coordinates are not the problem and the context state is.
const trailDiagOpBounds = { minX: Infinity, minY: Infinity, maxX: -Infinity, maxY: -Infinity, r: 0, n: 0 };

function noteTrailOpBounds(ops) {
  if (!TRAIL_DIAG) return;
  for (const op of ops) {
    const xs = [Number(op.x1), Number(op.x2), Number(op.x)];
    const ys = [Number(op.y1), Number(op.y2), Number(op.y)];
    for (const x of xs) if (Number.isFinite(x)) {
      trailDiagOpBounds.minX = Math.min(trailDiagOpBounds.minX, x);
      trailDiagOpBounds.maxX = Math.max(trailDiagOpBounds.maxX, x);
    }
    for (const y of ys) if (Number.isFinite(y)) {
      trailDiagOpBounds.minY = Math.min(trailDiagOpBounds.minY, y);
      trailDiagOpBounds.maxY = Math.max(trailDiagOpBounds.maxY, y);
    }
    const r = Number(op.r);
    if (Number.isFinite(r)) trailDiagOpBounds.r = Math.max(trailDiagOpBounds.r, r);
    trailDiagOpBounds.n += 1;
  }
}
let trailDiagEl = null;
let trailDiagAt = 0;

function paintedPixels(canvas) {
  if (!canvas?.width) return -1;
  try {
    const ctx = canvas.getContext('2d');
    const data = ctx.getImageData(0, 0, canvas.width, canvas.height).data;
    let painted = 0;
    for (let i = 3; i < data.length; i += 400) if (data[i] > 30) painted += 1;
    return painted;
  } catch (_) {
    return -2;
  }
}

// A SQUARE YOU CAN SEE, drawn through exactly the path the trail uses: the same
// context, the same transform, in field coordinates. It separates the two
// remaining stories, which no number can:
//
//   square visible   -> the canvas paints and is on screen, so the trail ops
//                       themselves are what is wrong
//   square invisible, pixels reported -> it paints and is not shown: something
//                       covers it, or its layer is never presented
//   square invisible, no pixels       -> the draw does not land at all
function drawTrailProbe() {
  const surface = trailSurfacesByPercent.values().next().value;
  const ctx = surface ? trailSurfaceCtx(surface) : null;
  if (!ctx) return 'no ctx';
  ctx.save();
  ctx.globalAlpha = 1;
  ctx.globalCompositeOperation = 'source-over';
  ctx.fillStyle = '#ff00ff';
  ctx.fillRect(8, 8, 60, 60);
  ctx.restore();
  return 'drawn at 8,8 60x60';
}

function renderTrailDiag(nowMs) {
  if (!TRAIL_DIAG) return;
  if (nowMs - trailDiagAt < 1000) return;
  trailDiagAt = nowMs;
  try {
    renderTrailDiagPanel(nowMs);
  } catch (error) {
    // A diagnostic that takes the renderer down with it is worse than no
    // diagnostic: report the failure in the panel's own place and carry on.
    if (typeof document !== 'undefined') {
      document.title = `diag error: ${error?.message || error}`;
    }
  }
}

function renderTrailDiagPanel(nowMs) {
  void nowMs;
  const probe = drawTrailProbe();
  if (!trailDiagEl) {
    trailDiagEl = document.createElement('pre');
    trailDiagEl.id = 'trailDiag';
    trailDiagEl.style.cssText = 'position:fixed;left:4px;top:4px;z-index:9999;margin:0;padding:6px 8px;'
      + 'background:rgba(0,0,0,0.82);color:#8f8;font:11px/1.35 monospace;white-space:pre;pointer-events:none;'
      + 'border:1px solid #3a3;border-radius:6px;max-width:96vw;';
    document.body.appendChild(trailDiagEl);
  }
  const lines = [];
  for (const surface of trailSurfacesByPercent.values()) {
    // Peak, not just now: a solo player who dies clears their own trail, so a
    // reading of zero at one instant proves nothing. A peak that never leaves
    // zero proves a great deal.
    const px = paintedPixels(surface.canvas);
    surface.peakPx = Math.max(Number(surface.peakPx) || 0, px);
    const canvas = surface.canvas;
    const attrs = surface.ctx?.getContextAttributes?.() || {};
    lines.push(`vis ${surface.percent}% ${canvas.width}x${canvas.height}`
      + ` px=${px} peak=${surface.peakPx} cpu=${attrs.willReadFrequently === true}`
      + ` desync=${attrs.desynchronized} op=${canvas.style.opacity || '1'}`);
  }
  if (trailSurfacesByPercent.size === 0) lines.push('vis: NO SURFACE');
  for (const [ownerId, layer] of trailLayersByOwner) {
    const px = paintedPixels(layer?.canvas);
    if (layer) layer.peakPx = Math.max(Number(layer.peakPx) || 0, px);
    lines.push(`layer ${ownerId} ${layer?.canvas?.width}x${layer?.canvas?.height}`
      + ` px=${px} peak=${layer?.peakPx ?? 0} kind=${layer?.canvas?.tagName ? 'dom' : 'offscreen'}`);
  }
  if (trailLayersByOwner.size === 0) lines.push('layers: none');
  lines.push(`ops=${trailDiagCounts.ops} composites=${trailDiagCounts.composites}`
    + ` clears=${trailDiagCounts.clears} erases=${trailDiagCounts.erases}`);
  // The state the strokes are actually being made in. A transform of 0, a
  // globalAlpha of 0 or a strokeStyle that is not what was asked for all paint
  // exactly nothing while the op counter climbs.
  const stateSurface = trailSurfacesByPercent.values().next().value;
  const ctx = stateSurface ? trailSurfaceCtx(stateSurface) : null;
  if (ctx) {
    const t = ctx.getTransform ? ctx.getTransform() : { a: NaN, d: NaN, e: NaN, f: NaN };
    const style = ctx.strokeStyle;
    lines.push(`tf a=${(+t.a).toFixed(3)} d=${(+t.d).toFixed(3)} e=${(+t.e).toFixed(1)} f=${(+t.f).toFixed(1)}`
      + ` ga=${ctx.globalAlpha} gco=${ctx.globalCompositeOperation}`);
    lines.push(`stroke=${typeof style === 'string' ? style : (style?.constructor?.name || 'obj')}`
      + ` lw=${(+ctx.lineWidth).toFixed(2)}`);
  }
  const b = trailDiagOpBounds;
  lines.push(`ops x ${b.minX === Infinity ? '-' : b.minX.toFixed(0)}..${b.maxX === -Infinity ? '-' : b.maxX.toFixed(0)}`
    + ` y ${b.minY === Infinity ? '-' : b.minY.toFixed(0)}..${b.maxY === -Infinity ? '-' : b.maxY.toFixed(0)}`
    + ` r=${b.r.toFixed(1)} field=${renderSurface.fieldWidth}x${renderSurface.fieldHeight}`);
  lines.push(`PROBE: magenta square ${probe} -- do you SEE it?`);
  // If the square is not on screen, ask the page WHY: what the canvas's own
  // styles are, and which element is actually on top at the square's position.
  const surfaceCanvas = stateSurface?.canvas || trailCanvas;
  if (surfaceCanvas) {
    const cs = getComputedStyle(surfaceCanvas);
    const rect = surfaceCanvas.getBoundingClientRect();
    lines.push(`css vis=${cs.visibility} op=${cs.opacity} z=${cs.zIndex} disp=${cs.display}`
      + ` filter=${cs.filter} tr=${cs.transform === 'none' ? 'none' : 'set'}`);
    lines.push(`rect ${Math.round(rect.left)},${Math.round(rect.top)}`
      + ` ${Math.round(rect.width)}x${Math.round(rect.height)}`
      + ` heads=${Math.round(headsCanvas?.getBoundingClientRect().width || 0)}`);
    // The square sits at field 8,8..68,68; find its middle on screen.
    const scaleX = rect.width / Math.max(1, renderSurface.fieldWidth);
    const scaleY = rect.height / Math.max(1, renderSurface.fieldHeight);
    const px = rect.left + 38 * scaleX;
    const py = rect.top + 38 * scaleY;
    // Not elementFromPoint: these canvases are pointer-events:none, so hit
    // testing always answers with the container and never with whatever is
    // painted over them. Ask instead which elements OVERLAP the square and
    // paint something -- an opaque background above z-index 2 would hide it.
    const covers = [];
    for (const el of document.body.querySelectorAll('canvas, div, aside, section, main')) {
      if (el === surfaceCanvas || el.contains(surfaceCanvas)) continue;
      const r = el.getBoundingClientRect();
      if (r.width < 8 || r.height < 8) continue;
      if (r.left > px || r.right < px || r.top > py || r.bottom < py) continue;
      const s2 = getComputedStyle(el);
      if (s2.visibility === 'hidden' || s2.display === 'none' || Number(s2.opacity) === 0) continue;
      const bg = s2.backgroundColor;
      const paints = bg && bg !== 'transparent' && !bg.endsWith(', 0)');
      const z = s2.zIndex === 'auto' ? 0 : Number(s2.zIndex);
      if (!paints && el.tagName !== 'CANVAS') continue;
      covers.push(`${el.id || String(el.className).split(' ')[0] || el.tagName}(z${z}${paints ? ',bg' : ''})`);
    }
    lines.push(`over the square: ${covers.slice(0, 5).join(' ') || 'nothing'}`);
  }
  let canvasBytes = 0;
  for (const canvas of document.querySelectorAll('canvas')) canvasBytes += canvas.width * canvas.height * 4;
  for (const layer of trailLayersByOwner.values()) {
    if (layer?.canvas && !layer.canvas.isConnected) canvasBytes += layer.canvas.width * layer.canvas.height * 4;
  }
  lines.push(`canvas memory ~${(canvasBytes / 1048576).toFixed(1)}MB`
    + ` budget=${LOW_CANVAS_BUDGET ? `low(${LOW_CANVAS_BUDGET})` : 'full'}`);
  lines.push(`arena=${ARENA_MODE}${ARENA_MERGED ? ' (one visible canvas)' : ' (stacked)'}`);
  lines.push(`dpr=${(window.devicePixelRatio || 1).toFixed(2)}`
    + ` backing=${renderSurface.backingWidth}x${renderSurface.backingHeight}`
    + ` scale=${(+renderSurface.scaleX).toFixed(3)}`);
  trailDiagEl.textContent = lines.join('\n');
}

function drawHeadShape(ctx, player, headRadius, playerColor) {
  if (teamHeadShapeFor(player?.team, localPlayerTeam()) === 'square') {
    // Side chosen to match the circle's area, so an opponent's head does not
    // read as heavier than a team-mate's, and inset with a darker square so it
    // still reads as a head rather than a block of trail.
    const side = headRadius * 1.72;
    ctx.fillStyle = playerColor;
    ctx.fillRect(-side / 2, -side / 2, side, side);
    ctx.strokeStyle = 'rgba(6, 10, 16, 0.55)';
    ctx.lineWidth = Math.max(0.6, headRadius * 0.16);
    ctx.strokeRect(-side * 0.29, -side * 0.29, side * 0.58, side * 0.58);
    return;
  }
  fillCenteredCircle(ctx, headRadius, playerColor);
}

function playerLeavesNoTrail(player) {
  return player?.drawingTrail === false
    || Boolean(player?.isFlying)
    || Boolean(player?.isGapping);
}

// The pose being drawn and the flags deciding whether to draw come from
// different instants: the head is sampled (or predicted) ahead of the newest
// snapshot, while the gap flags ride that snapshot. In the window before a
// freshly opened gap is reflected in the sampled state, the bridge keeps
// extending toward a head that is already inside the hole, and paints trail
// across it -- a flicker of solid line where the gap should be. Consulting the
// authoritative pose as well closes that window: a gap the server has already
// committed to suppresses the bridge immediately.
function ownerLeavesNoTrail(player, ownerId) {
  if (playerLeavesNoTrail(player)) return true;
  const players = authoritative.state?.players;
  if (!Array.isArray(players)) return false;
  const live = players.find((candidate) => Math.floor(Number(candidate?.id)) === ownerId);
  return Boolean(live) && playerLeavesNoTrail(live);
}

function drawLiveTrailBridge(ctx, player, headRadius, playerColor, alpha = 1) {
  const ownerId = Number(player?.id);
  if (!Number.isFinite(ownerId)) return;
  const owner = Math.floor(ownerId);
  if (ownerLeavesNoTrail(player, owner)) {
    // Drop the anchor too, so when drawing resumes the bridge cannot reach back
    // over the hole to where the trail left off. It is re-established by the
    // first trail op the server sends after the gap.
    trailAnchors.delete(owner);
    return;
  }
  const anchor = trailAnchors.get(owner);
  if (!anchor) return;

  const x = Number(player?.x);
  const y = Number(player?.y);
  const ax = Number(anchor.x);
  const ay = Number(anchor.y);
  if (!Number.isFinite(x) || !Number.isFinite(y) || !Number.isFinite(ax) || !Number.isFinite(ay)) return;

  const dist = Math.hypot(x - ax, y - ay);
  if (dist <= 0.05) return;
  if (dist > liveTrailBridgeMaxPx(player, headRadius)) return;

  ctx.save();
  // This stub lives on the heads canvas, which is fully opaque, so it carries
  // the trail's alpha itself -- otherwise the newest few pixels of trail would
  // be brighter than the rest of it.
  ctx.globalAlpha = alpha * slotTrailOpacity(player?.id);
  ctx.strokeStyle = playerColor;
  ctx.lineWidth = headRadius * 2;
  ctx.lineCap = 'butt';
  ctx.lineJoin = 'round';
  ctx.beginPath();
  ctx.moveTo(ax, ay);
  ctx.lineTo(x, y);
  ctx.stroke();
  ctx.restore();
}

function liveTrailBridgeMaxPx(player, headRadius) {
  const playerId = Number(player?.id);
  const isPredictedLocal = player?.localPrediction === true
    && Number.isFinite(playerId)
    && Number.isFinite(Number(net.localPlayerId))
    && playerId === Number(net.localPlayerId);
  const bridgeBudget = isPredictedLocal
    ? LOCAL_HEAD_ACTIVE_ATTACH_MAX_PX
    : REMOTE_LIVE_TRAIL_BRIDGE_MAX_PX;
  return Math.max(bridgeBudget, headRadius * 3.5);
}

function spawnMarkerVisible(player, nowMs, showSpawnMarkers) {
  if (!showSpawnMarkers || !player?.alive) return false;
  const spawnAtRaw = Number(player.spawnAtMs);
  const spawnAtMs = Number.isFinite(spawnAtRaw) ? spawnAtRaw : nowMs;
  const markerUntilRaw = Number(player.spawnMarkerUntilMs);
  const markerUntilMs = Number.isFinite(markerUntilRaw) && markerUntilRaw > 0
    ? markerUntilRaw
    : spawnAtMs + HEAD_SPAWN_MARKER_FALLBACK_MS;
  return nowMs < markerUntilMs;
}

function clampHeadDirtyRect(minX, minY, maxX, maxY) {
  const fieldWidth = Math.max(1, Number(model.config.fieldWidth) || DEFAULT_CONFIG.fieldWidth);
  const fieldHeight = Math.max(1, Number(model.config.fieldHeight) || DEFAULT_CONFIG.fieldHeight);
  const x = clamp(Math.floor(minX - HEAD_DIRTY_RECT_PADDING_PX), 0, fieldWidth);
  const y = clamp(Math.floor(minY - HEAD_DIRTY_RECT_PADDING_PX), 0, fieldHeight);
  const right = clamp(Math.ceil(maxX + HEAD_DIRTY_RECT_PADDING_PX), 0, fieldWidth);
  const bottom = clamp(Math.ceil(maxY + HEAD_DIRTY_RECT_PADDING_PX), 0, fieldHeight);
  if (right <= x || bottom <= y) return null;
  return { x, y, width: right - x, height: bottom - y };
}

function playerHeadDirtyRect(player, headRadius, nowMs, showSpawnMarkers, localPlayerId) {
  if (!player?.alive) return null;
  const x = Number(player.x);
  const y = Number(player.y);
  if (!Number.isFinite(x) || !Number.isFinite(y)) return null;

  let extent = headRadius + (player.isFlying ? 6 : 2);
  if (spawnMarkerVisible(player, nowMs, showSpawnMarkers)) {
    const isLocal = Number(player.id) === Number(localPlayerId);
    // The local marker also includes rotating LEFT/RIGHT labels. Use their
    // radial extent so diagonal headings cannot push corners outside cleanup.
    extent = Math.max(extent, isLocal ? localTurnHintDirtyExtent(headRadius) : headRadius + 32);
  }
  if (player?.effect?.type === PU_TYPES.SPEED && Number(player.effect.ticks) > 0) {
    const stacks = Math.min(EFFECT_RING_MAX_STACKS, speedEffectStackCount(player.effect));
    extent = Math.max(
      extent,
      headRadius
        + EFFECT_RING_DISTANCE_OFFSET
        + EFFECT_RING_DISTANCE_STEP * Math.max(0, stacks - 1)
        + EFFECT_RING_LINE_WIDTH
    );
  }
  // drawHeadsCore applies the spawn tween before drawing the head, direction
  // arrow, local hints, and effect rings. Dirty bounds must use that exact
  // scale too; clearing only the unscaled marker leaves a copy every frame.
  extent = spawnTweenDirtyExtent(extent, player, nowMs, HEAD_SPAWN_RENDER_CONFIG, 3);

  let minX = x - extent;
  let minY = y - extent;
  let maxX = x + extent;
  let maxY = y + extent;

  if (player.drawingTrail !== false && !player.isFlying && !player.isGapping) {
    const ownerId = Number(player.id);
    const anchor = Number.isFinite(ownerId) ? trailAnchors.get(Math.floor(ownerId)) : null;
    const ax = Number(anchor?.x);
    const ay = Number(anchor?.y);
    if (Number.isFinite(ax) && Number.isFinite(ay)) {
      const bridgeDistance = Math.hypot(x - ax, y - ay);
      if (
        bridgeDistance > 0.05
        && bridgeDistance <= liveTrailBridgeMaxPx(player, headRadius)
      ) {
        minX = Math.min(minX, ax - headRadius);
        minY = Math.min(minY, ay - headRadius);
        maxX = Math.max(maxX, ax + headRadius);
        maxY = Math.max(maxY, ay + headRadius);
      }
    }
  }

  return clampHeadDirtyRect(minX, minY, maxX, maxY);
}

function gemCollectionEffectDirtyRect(effect) {
  const x = Number(effect?.x);
  const y = Number(effect?.y);
  if (!Number.isFinite(x) || !Number.isFinite(y)) return null;
  // Covers the expanding ring/shards plus the rising +score label.
  return clampHeadDirtyRect(x - 55, y - 62, x + 55, y + 30);
}

function clearPreviousHeadFrame() {
  if (!headsCtx) return;
  if (headsNeedFullClear || previousHeadFrameWasComplex) {
    clearCanvasContext(headsCtx);
    return;
  }
  for (let i = 0; i < previousHeadDirtyRects.length; i += 1) {
    const rect = previousHeadDirtyRects[i];
    headsCtx.clearRect(rect.x, rect.y, rect.width, rect.height);
  }
}

function drawHeads(nowMs = Date.now(), showSpawnMarkers = true) {
  if (!ensureHeadsContext()) return;
  syncTrailOpacityStyle();
  applyArenaLayerVisibility();
  renderTrailDiag(nowMs);
  const players = Array.isArray(model.state.players) ? model.state.players : [];
  syncMeshColors(players);
  // (also run before the trail flush in dispatchRenderFrame -- see there)
  const headRadius = (model.config.trailSize || 8) / 2;
  const localPlayerId = Number.isFinite(Number(net.localPlayerId))
    ? Number(net.localPlayerId)
    : 0;
  clearPreviousHeadFrame();

  drawHeadsCore({
    ctx: headsCtx,
    players,
    nowMs,
    fieldWidth: model.config.fieldWidth,
    fieldHeight: model.config.fieldHeight,
    headRadius,
    localPlayerId,
    showSpawnMarkers,
    spawn: HEAD_SPAWN_RENDER_CONFIG,
    effect: HEAD_EFFECT_RENDER_CONFIG,
    drawHead: drawHeadShape,
    drawTrailBridge: drawLiveTrailBridge,
    disconnectedColor: DISCONNECTED_COLOR,
    resolvePlayerColor: meshColorAtPosition,
    clearCanvas: false
  });
  drawGemCollectionEffects(headsCtx, nowMs);

  previousHeadDirtyRects = [
    ...players.map((player) => playerHeadDirtyRect(
      player,
      headRadius,
      nowMs,
      showSpawnMarkers,
      localPlayerId
    )),
    ...gemCollectionEffects.map(gemCollectionEffectDirtyRect)
  ].filter(Boolean);
  previousHeadFrameWasComplex = false;
  headsNeedFullClear = false;
}

function drawGemShard(ctx, x, y, size, angle, color) {
  ctx.save();
  ctx.translate(x, y);
  ctx.rotate(angle);
  ctx.fillStyle = color;
  ctx.beginPath();
  ctx.moveTo(0, -size);
  ctx.lineTo(size * 0.64, 0);
  ctx.lineTo(0, size);
  ctx.lineTo(-size * 0.64, 0);
  ctx.closePath();
  ctx.fill();
  ctx.restore();
}

function drawGemCollectionEffect(ctx, effect, ageMs) {
  const progress = clamp(ageMs / GEM_COLLECTION_EFFECT_DURATION_MS, 0, 1);
  const released = easeOutCubic(progress);
  const baseAngle = effect.seed * Math.PI / 180;
  const ringAlpha = (1 - progress) * 0.34;
  const rise = 24 * released;

  ctx.save();
  ctx.lineWidth = Math.max(0.8, 1.8 * (1 - progress));
  ctx.globalAlpha = ringAlpha;
  ctx.strokeStyle = effect.color;
  ctx.beginPath();
  ctx.arc(effect.x, effect.y, 5 + 17 * released, 0, Math.PI * 2);
  ctx.stroke();

  for (let i = 0; i < 3; i += 1) {
    const angle = baseAngle + (Math.PI * 2 / 3) * i;
    const distance = 8 + (8 + (i % 2) * 3) * released;
    const shardSize = Math.max(0.8, (3.6 - i * 0.35) * (1 - progress));
    drawGemShard(
      ctx,
      effect.x + Math.cos(angle) * distance,
      effect.y + Math.sin(angle) * distance - rise * 0.12,
      shardSize,
      angle + progress * 0.75,
      i % 2 === 0 ? effect.glow : effect.color
    );
  }

  const appear = clamp(progress / 0.18, 0, 1);
  const fade = 1 - clamp((progress - 0.5) / 0.5, 0, 1);
  const textScale = 0.8 + easeOutBack(Math.min(1, progress / 0.28)) * 0.2;
  const textDrift = Math.sin(baseAngle) * 2.5 * released;
  ctx.globalAlpha = appear * fade;
  ctx.translate(effect.x + textDrift, effect.y - 11 - rise);
  ctx.scale(textScale, textScale);
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.font = GEM_COLLECTION_POPUP_FONT;
  ctx.lineJoin = 'round';
  ctx.lineWidth = 3;
  ctx.strokeStyle = effect.shade;
  ctx.fillStyle = effect.color;
  ctx.strokeText(effect.label, 0, 0);
  ctx.fillText(effect.label, 0, 0);
  ctx.restore();
}

function drawGemCollectionEffects(ctx, nowMs) {
  if (!ctx || gemCollectionEffects.length === 0) return;
  const now = Number.isFinite(Number(nowMs)) ? Number(nowMs) : Date.now();
  let write = 0;
  for (let i = 0; i < gemCollectionEffects.length; i += 1) {
    const effect = gemCollectionEffects[i];
    const ageMs = Math.max(0, now - effect.startAtMs);
    if (ageMs >= GEM_COLLECTION_EFFECT_DURATION_MS) continue;
    gemCollectionEffects[write] = effect;
    write += 1;
    drawGemCollectionEffect(ctx, effect, ageMs);
  }
  gemCollectionEffects.length = write;
}

function dispatchRenderFrame(renderState, nowServer, showSpawnMarkers = true) {
  // Before the flush, not inside drawHeads afterwards: the ops in this frame
  // are painted by flushTrailOps, so a mesh registered later would leave the
  // first batch of a player's trail flat.
  syncMeshColors(renderState?.players || model.state?.players);
  flushTrailOps();
  drawPowerups(nowServer);
  drawHeads(nowServer, showSpawnMarkers);
  compositeArenaFrame();
}

// Input edges can land just after the browser's animation callback. Redraw only
// the dynamic player layer here; queued trail work stays on the next animation
// frame so a turn never waits behind network trail processing.
function dispatchPlayerFrame(renderState, nowServer, showSpawnMarkers = true) {
  void renderState;
  drawHeads(nowServer, showSpawnMarkers);
  compositeArenaFrame();
}

export {
  setupRenderBackend,
  setCanvasSize,
  fitStageToViewport,
  clearField,
  applyOps,
  dispatchRenderFrame,
  dispatchPlayerFrame,
  queueGemCollectionEffects,
  syncRenderPrefs
};
